<template>
  <KkBox>
    <template #header>
      <div class="nav">
        <div class="nav__button">
          <KkButton
            color="secondary"
            :to="{ name: 'CompaniesStorePostsIndex', params: { company_uuid: $route.params.company_uuid }}"
            :text="$t('labels.backToList')"
          />
        </div>
      </div>
    </template>
    <template #content>
      <KkLoading v-if="loading" min-height="120px" />

      <CompaniesStorePostsBase
        v-else
        ref="companiesStorePostsBase"
        :need-approval="needApproval"
        :is-post-edit="isPostEditMode"
        :available-services="availableServices"
        @change-services="changeServices"
        @open-store-modal="handleOpenStoreModal"
        @open-action-modal="openActionModal"
        @drafted-action="newDraftedPostSubmit"
      />

      <StoreModal
        v-if="showStoreModal"
        store-name="companiesStorePosts"
        @close-store-modal="handleClose"
        @back-filter-modal="handleBack()"
      />

      <KkPromotionCompaniesStorePostsActionModal
        v-if="!loading"
        :show-action-modal="showActionModal"
        @close-action-modal="closeActionModal"
        @new-post-submit="actionModalSubmit"
      />

      <LoadingModal
        :completed="loadingCompleted"
        :failed="loadingFailed"
        :show-loading-modal="showLoadingModal"
        @close-loading-modal="closeLoadingModal()"
      />

      <FilterModal
        v-if="modal"
        @close="handleCloseFilterModal"
        @submit="handleSubmit"
        @reset="handleReset"
      />
    </template>
  </KkBox>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { KkBox, KkButton, KkLoading } from '../components/base'
import { KkPromotionCompaniesStorePostsActionModal } from '../components/domains/promotion/CompaniesStorePosts'
import CompaniesStorePostsBase from '../components/CompaniesStorePosts/CompaniesStorePostsBase'
import StoreModal from '../components/StoreModal'
import FilterModal from '../components/CompaniesStorePosts/FilterModal'
import LoadingModal from '../components/CompaniesStorePosts/LoadingModal'
import { toSnakeCase } from '../modules/string_converter'
import { getMediaImage } from '../modules/getMediaImage'

export default {
  components: {
    KkBox,
    KkButton,
    KkLoading,
    KkPromotionCompaniesStorePostsActionModal,
    CompaniesStorePostsBase,
    StoreModal,
    LoadingModal,
    FilterModal,
  },
  data() {
    return {
      loading: true,
      showStoreModal: false,
      showActionModal: false,
      showLoadingModal: false,
      loadingCompleted: false,
      loadingFailed: false,
      modal: false,
      currentUser: gon.currentUser,
    }
  },
  computed: {
    ...mapState({
      brands: state => state.companiesSearchTagGroups.data.brands,
      franchisees: state => state.companiesSearchTagGroups.data.franchisees,
      areas: state => state.companiesSearchTagGroups.data.areas,
      userCompanies: state => state.companiesSearchTagGroups.data.user_companies,
      prefectures : state => state.companiesSearchTagGroups.data.prefectures,
      originalTagGroupA : state => state.companiesSearchTagGroups.data.original_tag_group_a,
      originalTagGroupB : state => state.companiesSearchTagGroups.data.original_tag_group_b,
      originalTagGroupC : state => state.companiesSearchTagGroups.data.original_tag_group_c,
      originalTagGroupD : state => state.companiesSearchTagGroups.data.original_tag_group_d,
      originalTagGroupE : state => state.companiesSearchTagGroups.data.original_tag_group_e,
      formValues:  state => state.companiesGmbSearchAttributes.formValues,
      storeData:  state => state.companiesGmbSearchStores.data,
      searchStoreLoading:  state => state.companiesGmbSearchStores.loading,
      post: state => state.companiesStorePosts.post,
      eventStartedTime: state => state.companiesStorePosts.eventStartedTime,
      eventEndedTime: state => state.companiesStorePosts.eventEndedTime,
      reservationDateTime: state => state.companiesStorePosts.reservationDateTime,
      company: state => state.companiesCompany.data,
      approvalRequestNotes: state => state.companiesStorePosts.approvalRequestNotes,
      repeatingPost: state => state.companiesStorePosts.repeatingPost,
      repeatingPostScheduleKind: state => state.companiesStorePosts.repeatingPostScheduleKind
    }),
    ...mapGetters({
      hasDocomoStore: 'companiesCompany/hasDocomoStore',
      hasYahooPlaceStore: 'companiesCompany/hasYahooPlaceStore',
      hasGmbStore: 'companiesCompany/hasGmbStore',
      selectedTopicType: 'companiesStorePosts/selectedTopicType',
      selectTopicType: 'companiesStorePosts/selectTopicType',
      eventStartedAt: 'companiesStorePosts/eventStartedAt',
      eventEndedAt: 'companiesStorePosts/eventEndedAt',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      showSubmitActionModalType: 'companiesStorePosts/showSubmitActionModalType',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
    }),
    isPostEditMode() {
      return !!this.$route.params.post_id
    },
    isPostCopyMode() {
      return !!this.$route.query.copy
    },
    isDraftedPostEditMode() {
      return !!this.$route.params.drafted_post_id
    },
    isDraftedPostCopyMode() {
      return !!this.$route.query.drafted_post_copy
    },
    isRequestingPostEditMode(){
      return !!this.$route.params.requesting_post_id || this.post.notes.length > 0
    },
    isRequestingPostOwnerEditMode(){
      // 申請中の投稿をオーナー権限のユーザーが編集する場合（申請中 or リジェクト中かどうかに関わらず）
      return this.isRequestingPostEditMode && this.showOnlyOwnerAuthority
    },
    isRequestingPostCopyMode(){
      return !!this.$route.query.requesting_post_copy
    },
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    needApproval() {
      return this.company.attributes.post_approval_flow && !this.showOnlyOwnerAuthority
    },
    availableServices() {
      if (this.isDocomoPlan) return ['docomo']

      let availableServices = []
      if (this.hasGmbStore) availableServices.push('google_map')
      if (this.hasDocomoStore) availableServices.push('docomo')
      if (this.hasYahooPlaceStore) availableServices.push('yahoo_map')

      return availableServices
    },
    isOnlyYahooMapPost() {
      return this.post.services.length === 1 && this.post.services.includes('yahoo_map')
    },
  },
  watch: {
    // バグなのかbeforeRouteUpdateが発火しなかったのでこうした
    $route() {
      this.initParams()
    },
  },
  async created() {
    this.initParams()
  },
  methods: {
    ...mapActions({
      readSearchTagGroups: 'companiesSearchTagGroups/readSearchTagGroups',
      readStores: 'companiesGmbSearchStores/readStores',
      getPost: 'companiesStorePosts/getPost',
      createPost: 'companiesStorePosts/createPost',
      updatePost: 'companiesStorePosts/updatePost',
      createPostAfterDraft: 'companiesStorePosts/createPostAfterDraft',
      createPreview: 'companiesStorePosts/createPreview',
      createRequestingPostPreview: 'companiesStorePosts/createRequestingPostPreview',
      getDraftedPost: 'companiesStorePosts/readDraftedPost',
      getRequestingPost: 'companiesStorePosts/getRequestingPost',
      createDraftedPost: 'companiesStorePosts/createDraftedPost',
      returnToDraftedPost: 'companiesStorePosts/returnToDraftedPost',
      returnRequestingPostToDraftedPost: 'companiesStorePosts/returnRequestingPostToDraftedPost',
      updateDraftedPost: 'companiesStorePosts/updateDraftedPost',
      createRequestingPost: 'companiesStorePosts/createRequestingPost',
      updateRequestingPost: 'companiesStorePosts/updateRequestingPost',
      createPostAfterRequesting: 'companiesStorePosts/createPostAfterRequesting',
      updateSubmitActionModalType: 'companiesStorePosts/updateSubmitActionModalType',
      readSnsCategories: 'companiesSnsCategories/readSnsCategories',
      postPostImage: 'companiesStorePosts/postPostImage',
      resetPostImages: 'companiesStorePosts/resetPostImages',
    }),
    ...mapMutations('companiesStorePosts', [
      'setFormValues',
      'updateStoreUuids',
      'resetPost',
    ]),
    ...mapMutations({
      setGmbSearchAttributesFormStoreValues: 'companiesGmbSearchAttributes/setFormValues',
      updateRepeatingPost: 'companiesStorePosts/updateRepeatingPost',
      updateRepeatingPostScheduleKind: 'companiesStorePosts/updateRepeatingPostScheduleKind',
      updateServices: 'companiesStorePosts/updateServices',
    }),
    handleOpenStoreModal() {
      if (this.post.storeUuids.length !== 0) {
        this.setFormValues({ stores: [...this.post.storeUuids] })
        this.showStoreModal = true
      } else {
        this.modal = true
      }
    },
    async copyImages() {
      if (this.post.postImages.length) {
        const postImages = [...this.post.postImages]
        this.resetPostImages()
        const promises = postImages.map(async(postImage) => {
          // TODO: postImageがidとURLしか帰ってこないので、idを仕方なく入れているが、nameを入れたい
          const image = await getMediaImage({ name: postImage.id, url: postImage.url })
          const reader = new FileReader()
          reader.readAsDataURL(image)
          reader.onload = async () => {
            await this.postPostImage({
              companyUuid: this.currentCompanyUuid,
              // TODO: postImageがidとURLしか帰ってこないので、idを仕方なく入れているが、nameを入れたい。
              fileName: postImage.id + '.jpeg',
              postImage: reader.result
            })
          }
        })
        await Promise.all(promises)
      }
    },
    async initParams() {
      this.loading = true
      this.resetPost()

      await Promise.all([
        this.readSearchTagGroups(),
        this.readStores({
          companyUuid: this.currentCompanyUuid,
          formValues: {},
          excludeBusinessTypes: [],
          onlyConnectedGmb: false,
          onlyConnectedDocomo: false,
          onlyConnectedYahooPlace: false,
        }),
        this.readSnsCategories(),
      ])
      this.setGmbSearchAttributesFormStoreValues(this.formInitialValues())
      if (this.isPostEditMode) {
        const payload = {
          companyUuid: this.currentCompanyUuid,
          id: this.$route.params.post_id,
        }
        await this.getPost(payload)
        this.updateStoreUuids(this.post.storeUuids)
      } else if (this.isPostCopyMode) {
        const payload = {
          companyUuid: this.currentCompanyUuid,
          id: this.$route.query.copy,
          isCopied: true
        }
        await this.getPost(payload)
        this.copyImages()
        this.updateStoreUuids(this.post.storeUuids)
      } else if (this.isDraftedPostEditMode) {
        const payload = {
          id: this.$route.params.drafted_post_id,
        }
        await this.getDraftedPost(payload)
        this.updateStoreUuids(this.post.storeUuids)
      } else if (this.isDraftedPostCopyMode) {
        const payload = {
          id: this.$route.query.drafted_post_copy,
          isCopied: true
        }
        await this.getDraftedPost(payload)
        this.copyImages()
        this.updateStoreUuids(this.post.storeUuids)
      } else if (this.isRequestingPostEditMode) {
        const payload = {
          id: this.$route.params.requesting_post_id,
        }
        await this.getRequestingPost(payload)
        this.updateStoreUuids(this.post.storeUuids)
      } else if (this.isRequestingPostCopyMode) {
        const payload = {
          id: this.$route.query.requesting_post_copy,
          isCopied: true
        }
        await this.getRequestingPost(payload, true)
        this.copyImages()
        this.updateStoreUuids(this.post.storeUuids)
      }

      if(this.post.repeatConfiguration) {
        this.updateRepeatingPost(true)
        this.updateRepeatingPostScheduleKind(this.post.repeatConfiguration.kind)
      }

      this.initSelectedServices()

      this.loading = false
    },
    initSelectedServices() {
      if (this.isDocomoPlan) {
        this.handleUpdateServices(['docomo'])
        return
      }

      // 特定のサービスのみを対象とする投稿の場合は、そのサービスのみを選択状態にする
      if (this.availableServices.length === 1) {
        this.handleUpdateServices(this.availableServices)
      }
    },
    closeLoadingModal() {
      this.showLoadingModal = !this.showLoadingModal
      this.loadingCompleted = false
      this.loadingFailed = false
    },
    createPayloadForPost(reservationDateTime=null) {
      const post = {
        topic_type: this.post.topicType,
        store_uuids: this.post.storeUuids,
        category_id: this.post.category.id,
        services: this.post.services,
      }
      if (reservationDateTime) post['scheduled_at'] = this.reservationDateTime?.toISOString()

      if (this.post.services.includes('docomo')) {
        post.docomo = { post_to_brand_page: this.post.docomo.postToBrandPage }
      }

      if (this.repeatingPost) post['repeat_configuration'] = { kind: toSnakeCase(this.repeatingPostScheduleKind) }

      const setCallToAction = () => {
        // yahooプレイス投稿のみの場合はactionTypeをセットしない
        if (this.isOnlyYahooMapPost) return

        // ドコモ投稿を含んでいる時はactionTypeは任意になるが、バックエンド側のバリデーションでエラーになるので、LEARN_MORE（詳細）をセットしておく
        if (this.post.callToAction.actionType === 'ACTION_TYPE_UNSPECIFIED' && this.post.services.includes('docomo')) {
          post['call_to_action'] = {
            action_type: 'LEARN_MORE',
            url: this.post.callToAction.url
          }
        } else if (this.post.callToAction.actionType !== 'ACTION_TYPE_UNSPECIFIED') {
          post['call_to_action'] = {
            action_type: this.post.callToAction.actionType,
            url: this.post.callToAction.url
          }
        }
      }

      switch(this.post.topicType) {
      case 'STANDARD':
        if (this.post.summary) post['summary'] = this.post.summary
        if (this.post.title) post['title'] = this.post.title
        if (this.post.postImages.length) post['post_images'] = this.post.postImages.map(postImage => {
          return {
            id: postImage.id,
            url: postImage.url
          }
        })
        setCallToAction()
        break
      case 'EVENT':
        post['event'] = {
          title: this.post.event.title,
          started_at: this.eventStartedAt && this.eventStartedAt.toISOString(),
          ended_at: this.eventEndedAt && this.eventEndedAt.toISOString(),
        }
        if (this.post.postImages.length) post['post_images'] = this.post.postImages.map(postImage => {
          return {
            id: postImage.id,
            url: postImage.url
          }
        })
        if (this.post.summary) post['summary'] = this.post.summary
        setCallToAction()
        break
      case 'OFFER':
        post['event'] = {
          title: this.post.event.title,
          started_at: this.eventStartedAt.toISOString(),
          ended_at: this.eventEndedAt.toISOString(),
        }
        if (this.post.postImages.length) post['post_images'] = this.post.postImages.map(postImage => {
          return {
            id: postImage.id,
            url: postImage.url
          }
        })
        post['offer'] = {}
        if (this.post.offer.couponCode) post.offer['coupon_code'] = this.post.offer.couponCode
        if (this.post.offer.redeemOnlineUrl) post.offer['redeem_online_url'] = this.post.offer.redeemOnlineUrl
        if (this.post.offer.termsConditions) post.offer['terms_conditions'] = this.post.offer.termsConditions
        if (this.post.summary) post['summary'] = this.post.summary
      }

      return post
    },
    async openActionModal() {
      const post = this.createPayloadForPost(this.reservationDateTime)

      // previewのAPIに繰り返し投稿パラメータ渡すとエラーが起きるため除去しておく
      delete post.repeat_configuration

      const payload = {}

      if(this.isPostEditMode){
        payload['post_id'] = this.$route.params.post_id
      }

      let previewRes
      if(this.showSubmitActionModalType === 'draft'){
        payload['post'] = post
        previewRes = await this.createPreview(payload)
      } else {
        if (this.needApproval) {
          // 申請経由のパターン
          payload['requesting_post'] = post
          payload['requesting_post_id'] = this.$route.params.drafted_post_id
          previewRes = await this.createRequestingPostPreview(payload)
        } else {
          // オーナーが投稿する or 承認申請機能が有効でない
          payload['post'] = post
          previewRes = await this.createPreview(payload)
        }
      }
      if(previewRes){
        if (!this.confirmNotToPostForServices(this.showSubmitActionModalType === 'draft')) return

        this.showActionModal = !this.showActionModal
      }
    },
    confirmNotToPostForServices(isDrafted = false) {
      const noPostServices = []
      // 投稿先サービスに対象店舗がないか判定
      if (this.post.services.includes('google_map') && !this.post.stores.length) noPostServices.push('googleMap')
      if (this.post.services.includes('docomo') && !this.post.docomoStores.length) noPostServices.push('docomo')
      if (!noPostServices.length) return true

      const confirmTextLabel = isDrafted ? 'confirmNotToPostForServicesWhenDrafted' : 'confirmNotToPostForServices'

      return confirm(this.$t(`pages.CompaniesStorePost.${confirmTextLabel}`, { services: noPostServices.map(s => this.$t(`pages.CompaniesStorePost.service.${s}`)).join('・') }))
    },
    actionModalSubmit(reservationDateTime){
      switch (this.showSubmitActionModalType) {
      case 'post':
        this.newPostSubmit(reservationDateTime)
        break
      case 'draft':
        this.newDraftedPostSubmit(reservationDateTime)
        break
      case 'request':
        this.newRequestingPostSubmit(reservationDateTime)
        break
      }
    },
    closeActionModal() {
      this.showActionModal = false
      this.updateSubmitActionModalType = ''
    },
    async newPostSubmit(reservationDateTime) {
      this.closeActionModal()
      const validate = await this.$refs.companiesStorePostsBase.validate()

      if (!validate) {
        return alert(this.$t('messages.invalid'))
      }

      this.showLoadingModal = true

      const post = this.createPayloadForPost(reservationDateTime)

      let isSuccess
      if(this.isPostEditMode){
        isSuccess = await this.updatePost({values: post, id: this.post.id})
      } else if(this.isRequestingPostOwnerEditMode) {
        isSuccess = await this.createPostAfterRequesting({payload: { post: post }, id: this.post.id})
      } else if(this.isDraftedPostEditMode) {
        isSuccess = await this.createPostAfterDraft({payload: { post: post }, draftedPostId: this.post.id})
      } else {
        isSuccess = await this.createPost({post: post})
      }

      if (isSuccess) {
        this.loadingCompleted = true
        setTimeout(
          () => {
            this.$router.push({ path: '/s/companies/' + this.currentCompanyUuid + '/posts' })
          },
          3000
        )
      } else {
        this.loadingFailed = true
      }
    },
    async newDraftedPostSubmit(reservationDateTime) {
      this.closeActionModal()
      const validate = await this.$refs.companiesStorePostsBase.validate()

      if (!validate) {
        return alert(this.$t('messages.invalid'))
      }

      this.showLoadingModal = true
      const post = this.createPayloadForPost(reservationDateTime)

      let isSuccess
      if (this.isPostEditMode) {
        if (this.isRequestingPostEditMode) {
          const confirmed = confirm(this.$t('pages.CompaniesStorePost.edit.confirmReturnRequestingPostToDraftedPost'))
          if (confirmed) {
            isSuccess = await this.returnToDraftedPost({
              id: this.$route.params.post_id,
              values: { post: post },
            })
          }
        } else {
          isSuccess = await this.returnToDraftedPost({
            id: this.$route.params.post_id,
            values: { post: post },
          })
        }
      } else if (this.isRequestingPostEditMode) {
        const confirmed = confirm(this.$t('pages.CompaniesStorePost.edit.confirmReturnRequestingPostToDraftedPost'))
        if (confirmed) {
          isSuccess = await this.returnRequestingPostToDraftedPost({
            id: this.$route.params.requesting_post_id,
            values: { post: post },
          })
        }
      } else if (this.isDraftedPostEditMode) {
        isSuccess = await this.updateDraftedPost({
          id: this.post.id,
          values: { post: post },
        })
      } else {
        isSuccess = await this.createDraftedPost({post: post})
      }

      if (isSuccess) {
        this.loadingCompleted = true
        setTimeout(
          () => {
            this.$router.push({ path: '/s/companies/' + this.currentCompanyUuid + '/posts' })
          },
          3000
        )
      } else {
        this.loadingFailed = true
      }
    },
    async newRequestingPostSubmit(reservationDateTime) {
      this.closeActionModal()
      const validate = await this.$refs.companiesStorePostsBase.validate()

      if (!validate) {
        return alert(this.$t('messages.invalid'))
      }

      this.showLoadingModal = true

      const post = this.createPayloadForPost(reservationDateTime)

      let isSuccess
      if(this.isRequestingPostEditMode || this.isDraftedPostEditMode) {
        isSuccess = await this.updateRequestingPost({
          id: this.post.id,
          values: { requesting_post: post, comment: this.approvalRequestNotes ?? '' },
        })
      } else {
        isSuccess = await this.createRequestingPost({
          requesting_post: post,
          comment: this.approvalRequestNotes
        })
      }

      if (isSuccess) {
        this.loadingCompleted = true
        setTimeout(
          () => {
            this.$router.push({ path: '/s/companies/' + this.currentCompanyUuid + '/posts' })
          },
          3000
        )
      } else {
        this.loadingFailed = true
      }
    },
    handleSubmit() {
      this.modal = false
      this.showStoreModal = true
    },
    handleCloseFilterModal() {
      this.setFormValues({ stores: [...this.post.storeUuids] })
      this.modal = false
    },
    handleReset() {
      this.modal = false
    },
    formInitialValues() {
      return {
        brands: this.brands.tags.map(v => v.id),
        franchisees: this.franchisees.tags.map(v => v.id),
        areas: this.areas.tags.map(v => v.id),
        user_companies: this.userCompanies.tags.map(v => v.id),
        prefectures: this.prefectures.tags.map(v => v.id),
        original_tag_group_a: this.originalTagGroupA?.tags.map(v => v.id),
        original_tag_group_b: this.originalTagGroupB?.tags.map(v => v.id),
        original_tag_group_c: this.originalTagGroupC?.tags.map(v => v.id),
        original_tag_group_d: this.originalTagGroupD?.tags.map(v => v.id),
        original_tag_group_e: this.originalTagGroupE?.tags.map(v => v.id)
      }
    },
    handleBack() {
      this.showStoreModal = false
      this.modal = true
    },
    handleClose(storeUuids) {
      this.showStoreModal = false
      this.updateStoreUuids(storeUuids)
    },
    changeServices(services) {
      this.handleUpdateServices(services)
    },
    handleUpdateServices(value) {
      this.setFormValues({ stores: [] })
      this.updateServices(value)
      const params = {
        companyUuid: this.currentCompanyUuid,
        formValues: {},
        excludeBusinessTypes: [],
      }
      if (value.length >= 2) {
        params.onlyConnectedGmb = false
        params.onlyConnectedDocomo = false
        params.onlyConnectedYahooPlace = false
      } else {
        params.onlyConnectedGmb = value.includes('google_map')
        params.onlyConnectedDocomo = value.includes('docomo')
        params.onlyConnectedYahooPlace = value.includes('yahoo_map')
      }
      this.readStores(params)
    },
  }
}
</script>

<style lang="scss" scoped>
.nav {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  &__button {
    margin-left: auto;
  }
}
</style>
